import React from 'react';
import Icon from '../../../components/AppIcon';

const MemberActivityBreakdown = ({ data }) => {
  if (!data) {
    return (
      <div className="bg-card rounded-xl shadow-md p-6">
        <h2 className="text-lg font-semibold text-foreground mb-4">Member Activity Breakdown</h2>
        <div className="flex flex-col items-center justify-center py-12">
          <Icon name="Users" size={48} className="text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No member activity data available</p>
        </div>
      </div>
    );
  }

  const membershipTypes = Object?.entries(data?.byMembershipType || {})?.map(([type, count]) => ({
    type,
    count
  }));

  const visitFrequency = [
    { label: 'Regular (15+ visits)', count: data?.byVisitFrequency?.regular || 0, color: 'var(--color-success)' },
    { label: 'Occasional (5-14 visits)', count: data?.byVisitFrequency?.occasional || 0, color: 'var(--color-warning)' },
    { label: 'Rare (< 5 visits)', count: data?.byVisitFrequency?.rare || 0, color: 'var(--color-destructive)' }
  ];

  const totalFrequency = visitFrequency?.reduce((sum, item) => sum + item?.count, 0);

  return (
    <div className="bg-card rounded-xl shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-foreground">Member Activity Breakdown</h2>
        <Icon name="Users" size={20} className="text-muted-foreground" />
      </div>
      {/* Membership Type Distribution */}
      <div className="mb-6">
        <h3 className="text-sm font-semibold text-foreground mb-3">By Membership Type</h3>
        <div className="space-y-3">
          {membershipTypes?.map((item, index) => {
            const total = membershipTypes?.reduce((sum, i) => sum + i?.count, 0);
            const percentage = total > 0 ? Math?.round((item?.count / total) * 100) : 0;
            
            return (
              <div key={index}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-foreground capitalize">{item?.type}</span>
                  <span className="text-sm font-semibold text-foreground">
                    {item?.count} ({percentage}%)
                  </span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div
                    className="bg-primary h-2 rounded-full transition-all"
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {/* Visit Frequency Distribution */}
      <div>
        <h3 className="text-sm font-semibold text-foreground mb-3">By Visit Frequency</h3>
        <div className="space-y-3">
          {visitFrequency?.map((item, index) => {
            const percentage = totalFrequency > 0 ? Math?.round((item?.count / totalFrequency) * 100) : 0;
            
            return (
              <div key={index}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-foreground">{item?.label}</span>
                  <span className="text-sm font-semibold text-foreground">
                    {item?.count} ({percentage}%)
                  </span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div
                    className="h-2 rounded-full transition-all"
                    style={{ width: `${percentage}%`, backgroundColor: item?.color }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {/* Summary Stats */}
      <div className="mt-6 pt-6 border-t border-border">
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center">
            <p className="text-2xl font-bold text-foreground">{totalFrequency}</p>
            <p className="text-xs text-muted-foreground">Total Members</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-success">
              {totalFrequency > 0 ? Math?.round((visitFrequency?.[0]?.count / totalFrequency) * 100) : 0}%
            </p>
            <p className="text-xs text-muted-foreground">Regular Visitors</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemberActivityBreakdown;